import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { Select } from "../../components/em/ui";
import { toast } from "../../utils/toast";
import { Toggle } from "./settingsUi";

// Add / edit a leave type and its policy: who it applies to, how days are
// credited, advance notice, and how unused balance expires. The server
// derives max_days_per_year from the allowance, so the yearly figure shown
// here is only a preview of what it will store.

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const FREQUENCIES = [
  { key: "monthly", label: "Monthly", per: "month", perYear: 12 },
  { key: "quarterly", label: "Quarterly", per: "quarter", perYear: 4 },
  { key: "upfront", label: "Upfront", per: null, perYear: 1 },
];
const EXPIRY_OPTIONS = [
  {
    key: "quarterly",
    title: "Lapses every quarter",
    desc: "Unused balance resets at the end of each quarter",
  },
  {
    key: "fixed_date",
    title: "Lapses on a fixed date",
    desc: "All unused balance expires once a year",
  },
  {
    key: "carry_forward",
    title: "Carry forward, then lapse",
    desc: "Keep a capped balance into next year; the rest expires",
  },
];

// Leap year so 29 February stays selectable.
const daysInMonth = (m) => new Date(2024, m, 0).getDate();
const isHalfStep = (n) => Number.isFinite(n) && Math.round(n * 2) === n * 2;
const fmtDays = (n) => (Number.isFinite(n) ? String(Number(n.toFixed(1))) : "—");
const plural = (n, word) => `${fmtDays(n)} ${word}${Number(n) === 1 ? "" : "s"}`;

const parseTypeIds = (raw) => {
  if (!raw) return null;
  try {
    const ids = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(ids) ? ids.map(Number) : null;
  } catch {
    return null;
  }
};

// Form state for a new type, or one loaded from an existing row. A row saved
// before the policy fields existed reads as "whole allowance upfront".
const initialForm = (item) => {
  if (!item) {
    return {
      name: "",
      color: "#ff0000",
      typeIds: null, // null = every employee type
      credit_days: "1",
      credit_frequency: "monthly",
      noticeOn: false,
      notice: "1",
      is_paid: true,
      expiry_rule: "fixed_date",
      lapse_day: "1",
      lapse_month: 4,
      carry_forward_max: "6",
    };
  }
  const notice = Number(item.min_advance_notice_days) || 0;
  return {
    name: item.name || "",
    color: item.color || "#ff0000",
    typeIds: parseTypeIds(item.applies_to_types),
    credit_days: String(
      Number(item.credit_frequency ? item.credit_days : item.max_days_per_year) || 12,
    ),
    credit_frequency: item.credit_frequency || "upfront",
    noticeOn: notice > 0,
    notice: String(notice || 1),
    is_paid: item.is_paid === undefined ? true : !!Number(item.is_paid),
    expiry_rule: item.expiry_rule || "fixed_date",
    lapse_day: String(item.lapse_day || 1),
    lapse_month: Number(item.lapse_month) || 4,
    carry_forward_max: String(item.carry_forward_max != null ? Number(item.carry_forward_max) : 6),
  };
};

/* ── Styles ─────────────────────────────────────────────────────────────── */
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  // Above the sticky .main-header (z-index 1000).
  zIndex: 1200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const card = {
  width: "min(480px,100%)",
  maxHeight: "calc(100vh - 48px)",
  display: "flex",
  flexDirection: "column",
  background: "var(--em-bg-card)",
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 20px 40px -12px rgba(0,0,0,0.25)",
};
const label = {
  display: "block",
  font: `600 13px ${T.font}`,
  color: T.fg1,
  marginBottom: 8,
};
const hint = { font: `400 11.5px ${T.font}`, color: T.fg5 };
const input = {
  width: "100%",
  height: 38,
  padding: "0 12px",
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  font: `400 13px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  background: "var(--em-bg-card)",
};
const section = { marginBottom: 20 };
const summaryKey = {
  font: `500 10px ${T.font}`,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: T.fg5,
  marginBottom: 2,
};
const summaryVal = { font: `500 12.5px/1.35 ${T.font}`, color: T.fg1 };

const Radio = ({ on }) => (
  <span
    style={{
      width: 16,
      height: 16,
      borderRadius: 9999,
      border: `1.5px solid ${on ? T.orange : T.fg7}`,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      marginTop: 1,
      boxSizing: "border-box",
    }}
  >
    {on && <span style={{ width: 8, height: 8, borderRadius: 9999, background: T.orange }} />}
  </span>
);

const LapseDateFields = ({ day, month, onDay, onMonth }) => (
  <div>
    <label style={{ ...label, font: `500 12px ${T.font}` }}>Lapse date</label>
    <div style={{ display: "flex", gap: 8 }}>
      <input
        type="number"
        min={1}
        max={daysInMonth(month)}
        value={day}
        onChange={(e) => onDay(e.target.value)}
        style={{ ...input, width: 64 }}
        aria-label="Lapse day"
      />
      <Select
        value={month}
        onChange={(e) => onMonth(Number(e.target.value))}
        style={{ width: 130, height: 38 }}
        aria-label="Lapse month"
      >
        {MONTHS.map((m, i) => (
          <option key={m} value={i + 1}>
            {m}
          </option>
        ))}
      </Select>
    </div>
  </div>
);

const LeaveTypeFormModal = ({ item, onClose, onSaved }) => {
  const baseURL = `${API_URL}/api/settings/calendar`;
  const token = localStorage.getItem("token");
  const [form, setForm] = useState(() => initialForm(item));
  const [empTypes, setEmpTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    axios
      .get(`${API_URL}/api/settings/employment-types`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setEmpTypes([...(res.data || [])].sort((a, b) => a.id - b.id)))
      .catch((err) => console.error("Error fetching employee types", err));
  }, [token]);

  // Ids a saved row points at may have been deleted since; only count the
  // ones that still exist.
  const selectedIds = useMemo(() => {
    const all = empTypes.map((t) => t.id);
    if (form.typeIds === null) return all;
    return all.filter((id) => form.typeIds.includes(id));
  }, [form.typeIds, empTypes]);
  const allSelected = empTypes.length > 0 && selectedIds.length === empTypes.length;

  const toggleType = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    set({ typeIds: next.length === empTypes.length ? null : next });
  };

  const freq = FREQUENCIES.find((f) => f.key === form.credit_frequency) || FREQUENCIES[0];
  const credit = Number(form.credit_days);
  const perYear = credit * freq.perYear;
  const lapseDay = Number(form.lapse_day);
  const lapseText = `${lapseDay || "—"} ${MONTHS[form.lapse_month - 1]}`;
  const carry = Number(form.carry_forward_max);

  const setLapseMonth = (m) => {
    // Pull the day back into range, e.g. 31 → 30 when moving to April.
    const d = Math.min(Number(form.lapse_day) || 1, daysInMonth(m));
    set({ lapse_month: m, lapse_day: String(d) });
  };

  const summary = [
    [
      "Employee type",
      !empTypes.length || allSelected
        ? "All"
        : empTypes.filter((t) => selectedIds.includes(t.id)).map((t) => t.name).join(", ") || "—",
    ],
    ["Leave type", form.name.trim() || "—"],
    ["Per year", fmtDays(perYear)],
    ["Advance notice", form.noticeOn ? plural(Number(form.notice) || 0, "day") : "None"],
    ["Granted", freq.per ? `${fmtDays(credit)} per ${freq.per}` : `${fmtDays(credit)} upfront`],
    [
      "Expire",
      form.expiry_rule === "quarterly"
        ? "Lapses every quarter"
        : form.expiry_rule === "fixed_date"
          ? `Lapses on ${lapseText}`
          : `Up to ${fmtDays(carry)} carried forward; the rest lapse on ${lapseText}`,
    ],
  ];

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Please enter leave type name");
    if (empTypes.length && !selectedIds.length)
      return toast.error("Choose at least one employee type");
    if (!isHalfStep(credit) || credit <= 0)
      return toast.error("Days credited must be more than 0, in steps of 0.5");
    const notice = form.noticeOn ? Number(form.notice) : 0;
    if (!Number.isInteger(notice) || notice < 0)
      return toast.error("Minimum advance notice must be a whole number of days, 0 or more.");
    if (form.expiry_rule !== "quarterly") {
      if (!Number.isInteger(lapseDay) || lapseDay < 1 || lapseDay > daysInMonth(form.lapse_month))
        return toast.error("Lapse date is not a valid day of that month");
    }
    if (form.expiry_rule === "carry_forward" && (!isHalfStep(carry) || carry < 0))
      return toast.error("Carry forward must be 0 or more days, in steps of 0.5");

    const payload = {
      name,
      color: form.color,
      is_paid: form.is_paid ? 1 : 0,
      min_advance_notice_days: notice,
      // null = every employee type, including ones added later.
      applies_to_types: allSelected || !empTypes.length ? null : selectedIds,
      credit_days: credit,
      credit_frequency: form.credit_frequency,
      expiry_rule: form.expiry_rule,
      ...(form.expiry_rule !== "quarterly" && {
        lapse_day: lapseDay,
        lapse_month: form.lapse_month,
      }),
      ...(form.expiry_rule === "carry_forward" && { carry_forward_max: carry }),
    };

    const authH = { headers: { Authorization: `Bearer ${token}` } };
    setSaving(true);
    try {
      if (item) await axios.put(`${baseURL}/leave-types/${item.id}`, payload, authH);
      else await axios.post(`${baseURL}/leave-types`, payload, authH);
      toast.success(item ? "Leave type updated" : "Leave type added");
      onSaved();
    } catch (err) {
      console.error("Error saving leave type", err);
      toast.error(err?.response?.data?.error || "Failed to save leave type");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.divider}` }}>
          <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
            {item ? "Edit leave type" : "Add leave type"}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
          {/* Name + colour (colour control unchanged) */}
          <div style={{ ...section, display: "flex", gap: 16, alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={label}>Leave name</label>
              <input
                type="text"
                value={form.name}
                placeholder="e.g. Casual Leave"
                onChange={(e) => set({ name: e.target.value })}
                style={input}
              />
            </div>
            <div>
              <label style={label}>Color</label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => set({ color: e.target.value })}
                style={{
                  width: 56,
                  height: 38,
                  padding: 2,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "var(--em-bg-card)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* Applies to */}
          <div style={section}>
            <label style={label}>Applies to</label>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              {empTypes.length === 0 ? (
                <div style={{ padding: "12px 14px", ...hint }}>
                  No employee types configured — this leave applies to everyone.
                </div>
              ) : (
                <>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      background: T.bgMuted,
                      cursor: "pointer",
                      font: `500 13px ${T.font}`,
                      color: T.fg1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => set({ typeIds: e.target.checked ? null : [] })}
                      style={{ accentColor: T.orange, margin: 0 }}
                    />
                    <span style={{ flex: 1 }}>All employee types</span>
                    <span style={hint}>
                      {selectedIds.length} of {empTypes.length} selected
                    </span>
                  </label>
                  {empTypes.map((t) => (
                    <label
                      key={t.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 14px 10px 38px",
                        borderTop: `1px solid ${T.divider}`,
                        cursor: "pointer",
                        font: `400 13px ${T.font}`,
                        color: T.fg1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(t.id)}
                        onChange={() => toggleType(t.id)}
                        style={{ accentColor: T.orange, margin: 0 }}
                      />
                      {t.name}
                    </label>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Allowance */}
          <div
            style={{
              ...section,
              padding: 16,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              background: T.bgMuted,
            }}
          >
            <div style={{ font: `600 11px ${T.font}`, color: T.fg5, marginBottom: 12 }}>
              Allowance
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ width: 96 }}>
                <label style={{ ...label, font: `500 12px ${T.font}` }}>Days credited</label>
                <input
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={form.credit_days}
                  onChange={(e) => set({ credit_days: e.target.value })}
                  style={input}
                />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={{ ...label, font: `500 12px ${T.font}` }}>Frequency</label>
                <div
                  role="radiogroup"
                  style={{
                    display: "flex",
                    height: 38,
                    padding: 3,
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    background: "var(--em-bg-card)",
                    boxSizing: "border-box",
                  }}
                >
                  {FREQUENCIES.map((f) => {
                    const on = f.key === form.credit_frequency;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() =>
                          set({
                            credit_frequency: f.key,
                            // See the Expiry options: upfront can't lapse quarterly.
                            ...(f.key === "upfront" &&
                              form.expiry_rule === "quarterly" && { expiry_rule: "fixed_date" }),
                          })
                        }
                        style={{
                          flex: 1,
                          height: "100%",
                          lineHeight: "normal",
                          outline: "none",
                          border: 0,
                          borderRadius: 6,
                          padding: 0,
                          cursor: "pointer",
                          background: on ? T.orange : "transparent",
                          color: on ? "#fff" : T.fg3,
                          font: `${on ? 600 : 500} 12.5px ${T.font}`,
                          transition: "background 150ms ease-out",
                        }}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ ...hint, marginTop: 10 }}>
              {freq.per ? (
                <>
                  Employees receive <b style={{ color: T.fg1 }}>{plural(credit, "day")} per {freq.per}</b>,{" "}
                  <b style={{ color: T.fg1 }}>{plural(perYear, "day")}</b> per year.
                </>
              ) : (
                <>
                  Employees receive <b style={{ color: T.fg1 }}>{plural(credit, "day")}</b> upfront each year.
                </>
              )}{" "}
              Half days (0.5) are allowed.
            </div>
          </div>

          {/* Advance notice */}
          <div style={section}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>Advance notice</div>
                <div style={hint}>Minimum days before the leave start date</div>
              </div>
              <Toggle checked={form.noticeOn} onChange={(v) => set({ noticeOn: v })} />
            </div>
            {form.noticeOn && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={form.notice}
                  onChange={(e) => set({ notice: e.target.value })}
                  style={{ ...input, width: 96 }}
                />
                <span style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>days</span>
              </div>
            )}
          </div>

          {/* Paid */}
          <div style={{ ...section, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>Paid leave</div>
              <div style={hint}>Unpaid approved days count as loss-of-pay</div>
            </div>
            <Toggle checked={form.is_paid} onChange={(v) => set({ is_paid: v })} />
          </div>

          {/* Expiry */}
          <div style={section}>
            <label style={label}>Expiry</label>
            <div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {EXPIRY_OPTIONS.map((o) => {
                const on = o.key === form.expiry_rule;
                // An upfront grant lands once per balance period, so quarterly
                // lapsing would hand out the whole year every quarter.
                const disabled = o.key === "quarterly" && form.credit_frequency === "upfront";
                return (
                  <button
                    key={o.key}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={disabled}
                    title={disabled ? "Not available with an upfront allowance" : undefined}
                    onClick={() => set({ expiry_rule: o.key })}
                    style={{
                      opacity: disabled ? 0.5 : 1,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      width: "100%",
                      // The global `button` rule pins height/line-height to
                      // 40px, which pushed the description out of the card.
                      height: "auto",
                      lineHeight: "normal",
                      outline: "none",
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 10,
                      cursor: disabled ? "not-allowed" : "pointer",
                      border: `1px solid ${on ? T.orange : T.border}`,
                      background: on ? T.orange50 : "var(--em-bg-card)",
                      color: T.fg1,
                      transition: "border-color 150ms ease-out, background 150ms ease-out",
                    }}
                  >
                    <Radio on={on} />
                    <span>
                      <span style={{ display: "block", font: `600 13px/1.3 ${T.font}`, color: T.fg1 }}>
                        {o.title}
                      </span>
                      <span style={{ ...hint, display: "block", marginTop: 4, lineHeight: 1.4 }}>
                        {o.desc}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {form.expiry_rule !== "quarterly" && (
              <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
                {form.expiry_rule === "carry_forward" && (
                  <div>
                    <label style={{ ...label, font: `500 12px ${T.font}` }}>Carry forward up to</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={form.carry_forward_max}
                        onChange={(e) => set({ carry_forward_max: e.target.value })}
                        style={{ ...input, width: 80 }}
                      />
                      <span style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>days</span>
                    </div>
                  </div>
                )}
                <LapseDateFields
                  day={form.lapse_day}
                  month={form.lapse_month}
                  onDay={(v) => set({ lapse_day: v })}
                  onMonth={setLapseMonth}
                />
                <div style={{ ...hint, flexBasis: "100%", marginTop: -6 }}>
                  Unused balance expires at the start of this day, and the new
                  leave year begins on it.
                </div>
              </div>
            )}
          </div>

          {/* Policy summary */}
          <div
            style={{
              padding: 14,
              borderRadius: 12,
              border: `1px solid ${T.orangeEdge}`,
              background: T.orange50,
            }}
          >
            <div style={{ font: `600 11px ${T.font}`, color: T.fg3, marginBottom: 10 }}>
              Policy summary
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: "12px 14px",
              }}
            >
              {summary.map(([k, v]) => (
                <div key={k} style={{ minWidth: 0 }}>
                  <div style={summaryKey}>{k}</div>
                  <div style={{ ...summaryVal, overflowWrap: "anywhere" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 22px",
            borderTop: `1px solid ${T.divider}`,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "9px 16px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              cursor: "pointer",
              background: "var(--em-bg-card)",
              font: `600 14px ${T.font}`,
              color: T.fg1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "9px 20px",
              borderRadius: 8,
              border: 0,
              cursor: saving ? "default" : "pointer",
              opacity: saving ? 0.7 : 1,
              background: T.gradient,
              color: "#fff",
              font: `600 14px ${T.font}`,
              boxShadow: T.shadowBrand,
            }}
          >
            {saving ? "Saving…" : item ? "Save changes" : "Save leave type"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveTypeFormModal;
